import VueI18n, { LocaleMessageObject } from 'vue-i18n';
import Vue from 'vue';
import en from '@/locales/i18n/en.json';
import { formatString } from '@/common/bot/lib';
import { SupportedLanguages, LANGUAGES } from '@/locales/languages';
import { waitFor } from '@/common/utils';
import { analytics, CRITICAL_EVENTS } from '@/common/logs';
import { parseErrorMessage } from '@/common/error';
import { logger } from '@/common/api/logger';

/**
 * Custom translation function that can be access via `this.t` in Vue components. This is an extension to the underlying
 * `VueI18n` library. This can be used with new keys that are not yet defined in locales with default values.
 *
 * The return type is casted to string to help inside components. This means that the key should always resolve to a string.
 *
 * @param this Vue instance
 * @param key The key to echo
 * @param optsOrDefault The default string to echo or the formatting arguments.
 * @param def The default value to echo.
 * @returns string
 */
function translator(
  this: Vue,
  key: string,
  optsOrDefault?: (string[] | Record<string, string | boolean | number | null>) | string,
  def?: string
) {
  if (this.$te(key) || (this.$i18n.fallbackLocale && hasFallback(this, key))) {
    const args: [string] | [string, VueI18n.Values] =
      optsOrDefault && typeof optsOrDefault === 'object' ? [key, optsOrDefault] : [key];
    // eslint-disable-next-line prefer-spread
    return this.$t.apply(this, args as any) as string;
  }
  const str = (optsOrDefault && typeof optsOrDefault === 'string' && optsOrDefault) || def || '';
  const options = (optsOrDefault && typeof optsOrDefault === 'object' && optsOrDefault) || {};
  return formatString(str, options);
}

function hasFallback(instance: Vue, key: string) {
  const fallbackLocale = instance.$i18n.fallbackLocale;
  if (!fallbackLocale) return false;
  if (typeof fallbackLocale === 'string') {
    return instance.$te(key, fallbackLocale);
  }
  if (Array.isArray(fallbackLocale)) {
    return !!fallbackLocale.find((locale) => instance.$te(key, locale));
  }
  if (!Object.keys(fallbackLocale).length) {
    return false;
  }
  const locale = instance.$i18n.locale;
  const langList = (fallbackLocale[locale] || []).concat(fallbackLocale.default || []);
  return !!langList.find((locale) => instance.$te(key, locale));
}

Vue.use(VueI18n);

// This applies a helper function
Vue.use((Vue) => {
  Vue.prototype.t = translator;
});

// Conversation-only translator (respects detected language override)
Vue.use((Vue) => {
  Vue.prototype.tConversation = (key: string, values?: any) => tConversation(key, values);
});

export interface LanguageChangeEvent {
  current: SupportedLanguages;
  previous: SupportedLanguages;
}
export type LanguageChangeListener = ($event: LanguageChangeEvent) => unknown;

export const i18n = new VueI18n({
  locale: 'en',
  fallbackLocale: 'en',
  messages: { en },
});

const LANG_EVENT = 'pl:lang:changed';
const LANG_EVENT_BEFORE_CHANGE = 'pl:lang:before:change';

let conversationLocaleOverride: SupportedLanguages | null = null;

/**
 * Set of languages that are available.
 */
const available = new Set<SupportedLanguages>(['en']);
/**
 * An event bus used for triggering changes.
 */
const bus = new Vue();

/**
 * Helper utility that fire event when language is changed.
 * @param locale The language to change into.
 * @returns The language changed.
 */
async function change(locale: SupportedLanguages) {
  const previous = i18n.locale;

  previous !== locale && bus.$emit(LANG_EVENT_BEFORE_CHANGE, { previous, current: locale } as LanguageChangeEvent);

  // Make sure to execute the "before" listeners
  await waitFor(0);

  i18n.locale = locale;
  // Fire event only on change.
  previous !== locale && bus.$emit(LANG_EVENT, { previous, current: locale } as LanguageChangeEvent);
  return locale;
}

/**
 * Register an event handler whenever language changes. If you have actions depending upon
 * language change, you can use the `before` param to execute them, before the language change
 * is applied.
 *
 * NOTE: Does not work for the first language change.
 * @param callback The event handler
 * @param before Executes just before setting the locale.
 * @returns A function that removes the listener.
 */
export function addListener(callback: LanguageChangeListener, before = false) {
  bus.$on(before ? LANG_EVENT_BEFORE_CHANGE : LANG_EVENT, callback);
  return () => removeListener(callback, before);
}

/**
 * Removes the given event listener.
 * @param callback Event handler
 */
export function removeListener(callback: LanguageChangeListener, before = false) {
  bus.$off(before ? LANG_EVENT_BEFORE_CHANGE : LANG_EVENT, callback);
}

/**
 * Loads the locale for the given language.
 * We need to specify language in the webpackChunkName as webpack does not support dynamic string (at runtime) imports.
 * @param language The language to load.
 * @returns The locale.
 */
export async function loadLocale(language: string) {
  switch (language.toLowerCase()) {
    case 'en':
      return (await import(/* webpackChunkName: "lang-en" */ '@/locales/i18n/en.json')).default;
    case 'es':
      return (await import(/* webpackChunkName: "lang-es" */ '@/locales/i18n/es.json')).default;
    case 'es-mx':
      return (await import(/* webpackChunkName: "lang-es-mx" */ '@/locales/i18n/es-mx.json')).default;
    case 'fr':
      return (await import(/* webpackChunkName: "lang-fr" */ '@/locales/i18n/fr.json')).default;
    case 'fr-cm':
      return (await import(/* webpackChunkName: "lang-fr-cm" */ '@/locales/i18n/fr-cm.json')).default;
    case 'pt':
      return (await import(/* webpackChunkName: "lang-pt" */ '@/locales/i18n/pt.json')).default;
    case 'pt-mz':
      return (await import(/* webpackChunkName: "lang-pt-mz" */ '@/locales/i18n/pt-mz.json')).default;
    case 'sw':
      return (await import(/* webpackChunkName: "lang-sw" */ '@/locales/i18n/sw.json')).default;
    case 'af-za':
      return (await import(/* webpackChunkName: "lang-af" */ '@/locales/i18n/af-za.json')).default;
    default:
      throw new Error(`Unknown locale: ${language}`);
  }
}

/**
 * Ensure a locale dictionary is loaded into i18n without changing the app locale.
 */
export async function ensureLanguageLoaded(toLoad: SupportedLanguages): Promise<void> {
  if (available.has(toLoad)) return;

  const locales: LocaleMessageObject = await loadLocale(toLoad);
  i18n.setLocaleMessage(toLoad, locales);
  available.add(toLoad);
}

/**
 * Change the language of the application. The languages will be loaded asynchronously.
 *
 * @param language The language to change.
 * @returns A promise resolving the changed language.
 */
export async function setLanguage(toChange: SupportedLanguages | 'pt-br' | 'es-MX') {
  // TODO: Fix language code.
  const language: SupportedLanguages | any = toChange === 'pt-br' ? 'pt' : toChange;
  if (i18n.locale === language || available.has(language)) {
    return change(language);
  }

  try {
    const locales: LocaleMessageObject = await loadLocale(language);

    i18n.setLocaleMessage(language, locales);
    available.add(language);
    return change(language);
  } catch (e) {
    logger.error('[I18N] Error loading locales', { error: parseErrorMessage(e) });
    analytics.trackCriticalEvent({
      name: CRITICAL_EVENTS.CHATBOT_FAILED_LOAD_LOCALES,
      metadata: {
        error: parseErrorMessage(e),
      },
    });
  }
}

export function getCurrentLanguageName() {
  return getName(i18n.locale as SupportedLanguages);
}

export function getName(lang: SupportedLanguages) {
  return LANGUAGES[lang]?.name || '';
}

/**
 * Test if the given locale code is supported by the application.
 */
export function isSupported(toChange: string): toChange is SupportedLanguages | 'pt-br' {
  // TODO: Fix language code.
  const language = (toChange === 'pt-br' ? 'pt' : toChange) as SupportedLanguages;
  return Boolean(LANGUAGES[language]);
}

function getOrCreateI18n() {
  let apiCreated: {
    t: (
      key: string,
      optsOrDefault?: string | string[] | Record<string, string | number | boolean | null> | undefined,
      def?: string | undefined
    ) => string;
    $t: {
      (key: string, values?: VueI18n.Values | undefined): VueI18n.TranslateResult;
      (key: string, locale: string, values?: VueI18n.Values | undefined): VueI18n.TranslateResult;
    };
    $te: (key: string, locale?: string | undefined) => boolean;
    $tc: {
      (key: string, choice?: number | undefined, values?: VueI18n.Values | undefined): string;
      (key: string, choice: number, locale: string, values?: VueI18n.Values | undefined): string;
    };
  };

  return () => {
    return (
      apiCreated ||
      (apiCreated = (() => {
        const obj = {
          $t: ((...args: Parameters<typeof VueI18n.prototype.t>) => i18n.t(...args)) as typeof VueI18n.prototype.t,
          $te: ((...args: Parameters<typeof VueI18n.prototype.te>) => i18n.te(...args)) as typeof VueI18n.prototype.te,
          $tc: ((...args: Parameters<typeof VueI18n.prototype.tc>) => i18n.tc(...args)) as typeof VueI18n.prototype.tc,
        };

        return {
          ...obj,
          t: translator.bind(obj as Vue),
        };
      })())
    );
  };
}

export const useI18n = getOrCreateI18n();

export async function setConversationLocaleOverride(toChange: SupportedLanguages | null) {
  if (!toChange) {
    conversationLocaleOverride = null;
    return null;
  }

  if (!isSupported(toChange)) {
    conversationLocaleOverride = null;
    return null;
  }

  await ensureLanguageLoaded(toChange);
  conversationLocaleOverride = toChange;
  return conversationLocaleOverride;
}

export function tConversation(
  key: string,
  values?: (string[] | Record<string, string | boolean | number | null>) | undefined
) {
  const locale = conversationLocaleOverride;

  // Normal case: behave exactly like `i18n.t`
  if (!locale) {
    if (!values) return i18n.t(key);
    return i18n.t(key, values);
  }

  // Override case: force locale
  if (!values) return i18n.t(key, locale);
  return i18n.t(key, locale, values);
}

declare module 'vue/types/vue' {
  interface Vue {
    t: typeof translator;
    tConversation: typeof tConversation;
  }
}
